import {
  Route,
  Routes,
  Navigate,
  BrowserRouter,
} from 'react-router-dom';
import { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {
  ROUTES_FEEKAPP,
  ROUTES_MANAGER,
  ROUTES_AGNOSTIC,
  MANAGER_SUBDOMAIN,
} from 'constants/routes';

// Eager — necesarios en el primer render
import LandingFeekApp from 'screens/feekApp/Landing';
import OutletFeekapp from 'screens/feekApp/OutletFeekapp';

// Lazy — se descargan solo cuando el usuario visita la ruta
const Map = lazy(() => import('screens/feekApp/Map'));
const NotFound = lazy(() => import('screens/NotFound'));
const Info = lazy(() => import('screens/feekApp/Info'));
const Agenda = lazy(() => import('screens/feekApp/Agenda'));
const Explore = lazy(() => import('screens/feekApp/Explore'));
const LoginFeeker = lazy(() => import('screens/feekApp/Login'));
const SearchFeekApp = lazy(() => import('screens/feekApp/Search'));
const MapResults = lazy(() => import('screens/feekApp/MapResults'));
const Experience = lazy(() => import('screens/feekApp/Experience'));
const Payment = lazy(() => import('screens/manager/legacy/Payment'));
const LandingManager = lazy(() => import('screens/manager/Landing'));
const HomeManager = lazy(() => import('screens/manager/legacy/Home'));
const NotFoundFeekapp = lazy(() =>
  import('screens/feekApp/NotFound'),
);
const CampaignFeekApp = lazy(() =>
  import('screens/feekApp/Campaign'),
);
const Policy = lazy(() =>
  import('screens/TermsAndConditions/Policy'),
);
const LoginManager = lazy(() =>
  import('screens/manager/legacy/Login'),
);
const ShareAppDownload = lazy(() =>
  import('screens/ShareAppDownload'),
);
const Dashboard = lazy(() =>
  import('screens/manager/legacy/Dashboard'),
);
const Notifications = lazy(() =>
  import('screens/feekApp/Notifications'),
);
const ResetPassword = lazy(() =>
  import('screens/feekApp/ResetPassword'),
);
const ProfileFeeker = lazy(() =>
  import('screens/feekApp/ProfileFeeker'),
);
const Promotions = lazy(() =>
  import('screens/feekApp/Agenda/Promotions'),
);
const TermsAndConditions = lazy(() =>
  import('screens/TermsAndConditions'),
);
const AddCampaign = lazy(() =>
  import('screens/manager/legacy/AddCampaign'),
);
const Billing = lazy(() =>
  import('screens/manager/legacy/Payment/Billing'),
);
const FeekspotFeekApp = lazy(() =>
  import('screens/feekApp/ProfileFeekspot'),
);
const RegistrationFeekApp = lazy(() =>
  import('screens/feekApp/Registration'),
);
const CampaignsManager = lazy(() =>
  import('screens/manager/legacy/Campaigns'),
);
const Collaborators = lazy(() =>
  import('screens/manager/legacy/Collaborators'),
);
const ProfileFeekerEdit = lazy(() =>
  import('screens/feekApp/ProfileFeekerEdit'),
);
const ForgotPassword = lazy(() =>
  import('screens/manager/legacy/ForgotPassword'),
);
const Participations = lazy(() =>
  import('screens/feekApp/Agenda/Participations'),
);
const PaymentHistory = lazy(() =>
  import('screens/manager/legacy/PaymentsHistory'),
);
const ExploreRewards = lazy(() =>
  import('screens/feekApp/Explore/ExploreRewards'),
);
const ProfileCustomer = lazy(() =>
  import('screens/manager/legacy/ProfileCustomer'),
);
const ProfileFeekspot = lazy(() =>
  import('screens/manager/legacy/ProfileFeekspot'),
);
const RegistrationManager = lazy(() =>
  import('screens/manager/legacy/Registration'),
);
const ExplorePromotions = lazy(() =>
  import('screens/feekApp/Explore/ExplorePromotions'),
);
const CollaboratorNewPassword = lazy(() =>
  import('screens/manager/legacy/CollaboratorNewPassword'),
);
const RegistrationAddFeekspot = lazy(() =>
  import(
    'screens/manager/legacy/Registration/RegistrationAddFeekspot'
  ),
);
const SubscriptionFeekspotSuccess = lazy(() =>
  import(
    'screens/manager/legacy/Payment/SubscriptionFeekspotSuccess'
  ),
);
const RegistrationFeekspotCategories = lazy(() =>
  import(
    'screens/manager/legacy/Registration/RegistrationAddFeekspot'
  ),
);
const RegistrationAddFeekspotSuccess = lazy(() =>
  import(
    'screens/manager/legacy/Registration/RegistrationAddFeekspotSuccess'
  ),
);
const RegistrationVerificationEmailCodeManager = lazy(() =>
  import(
    'screens/manager/legacy/Registration/RegistrationVerificationEmailCode'
  ),
);
const RegistrationVerificationPhoneCodeManager = lazy(() =>
  import(
    'screens/manager/legacy/Registration/RegistrationVerificationPhoneCode'
  ),
);
const OutletManagerLegacy = lazy(() =>
  import('screens/manager/legacy/Outlet'),
);

const RootNavigator = () => {
  const userId = useSelector((state) => state.feekApp.id);

  return (
    <BrowserRouter>
      <Suspense fallback={null}>
        <Routes>
          {/* Auth agnostic */}
          <Route path={ROUTES_AGNOSTIC.POLICY} element={<Policy />} />
          <Route
            path={ROUTES_AGNOSTIC.TERMS_AND_CONDITIONS}
            element={<TermsAndConditions />}
          />
          <Route
            exact
            path={ROUTES_AGNOSTIC.SHARE_APP_DOWNLOAD}
            element={<ShareAppDownload />}
          />
          <Route
            exact
            path={ROUTES_AGNOSTIC.SHARE_APP_DOWNLOAD_PARAMS}
            element={<ShareAppDownload />}
          />

          {new RegExp(
            '^(?:www\\.)?' +
              MANAGER_SUBDOMAIN +
              '(feek\\.app|localhost)',
          ).test(window.location.hostname) ? (
            <>
              <Route path="*" element={<NotFound />} />

              <Route
                path={ROUTES_MANAGER.LANDING}
                element={<LandingManager />}
              />

              <Route
                path={ROUTES_MANAGER.COLLABORATOR_NEW_PASSWORD}
                element={<CollaboratorNewPassword />}
              >
                <Route
                  path={':account_invite'}
                  element={<CollaboratorNewPassword />}
                />
              </Route>

              <Route
                exact
                path={ROUTES_MANAGER.REGISTRATION}
                element={<RegistrationManager />}
              />

              <Route
                exact
                path={
                  ROUTES_MANAGER.REGISTRATION_VERIFICATION_CODE_EMAIL
                }
                element={<RegistrationVerificationEmailCodeManager />}
              />

              <Route
                exact
                path={
                  ROUTES_MANAGER.REGISTRATION_VERIFICATION_CODE_PHONE
                }
                element={<RegistrationVerificationPhoneCodeManager />}
              />

              <Route
                exact
                path={ROUTES_MANAGER.FORGOT_PASSWORD}
                element={<ForgotPassword />}
              />

              <Route
                path="/"
                element={
                  <AuthRoute manager>
                    <OutletManagerLegacy />
                  </AuthRoute>
                }
              >
                <Route
                  path={ROUTES_MANAGER.COLLABORATORS}
                  element={<Collaborators />}
                />
                <Route
                  path={ROUTES_MANAGER.CAMPAIGNS}
                  element={<CampaignsManager />}
                />
                <Route
                  path={ROUTES_MANAGER.ADD_CAMPAIGNS}
                  element={<AddCampaign />}
                />
                <Route
                  path={ROUTES_MANAGER.UPDATE_CAMPAIGNS}
                  element={<AddCampaign />}
                />
                <Route
                  path={ROUTES_MANAGER.PAYMENT_HISTORY}
                  element={<PaymentHistory />}
                />
                <Route
                  path={ROUTES_MANAGER.PROFILE_CUSTOMER}
                  element={<ProfileCustomer />}
                />
                <Route
                  path={ROUTES_MANAGER.HOME}
                  element={<HomeManager />}
                />
                <Route
                  path={ROUTES_MANAGER.DASHBOARD}
                  element={<Dashboard />}
                />
                <Route
                  path={ROUTES_MANAGER.PROFILE_FEEKSPOT}
                  element={<ProfileFeekspot />}
                />
                <Route
                  path={ROUTES_MANAGER.BILLING}
                  element={<Billing />}
                />
              </Route>

              <Route
                exact
                path={ROUTES_MANAGER.LOGIN}
                element={
                  <AuthRoute login manager>
                    <LoginManager />
                  </AuthRoute>
                }
              />

              <Route
                path={ROUTES_MANAGER.PAYMENT}
                element={
                  <AuthRoute manager>
                    <Payment />
                  </AuthRoute>
                }
              />

              <Route
                path={ROUTES_MANAGER.SUBSCRIPTION_SUCCESS}
                element={
                  <AuthRoute manager>
                    <SubscriptionFeekspotSuccess />
                  </AuthRoute>
                }
              />

              <Route
                exact
                path={ROUTES_MANAGER.REGISTRATION_FEEKSPOT}
                element={
                  <AuthRoute manager>
                    <RegistrationAddFeekspot />
                  </AuthRoute>
                }
              />

              <Route
                exact
                path={ROUTES_MANAGER.REGISTRATION_FEEKSPOT_SUCESS}
                element={
                  <AuthRoute manager>
                    <RegistrationAddFeekspotSuccess />
                  </AuthRoute>
                }
              />

              <Route
                exact
                path={ROUTES_MANAGER.REGISTRATION_FEEKSPOT_CATEGORIES}
                element={
                  <AuthRoute manager>
                    <RegistrationFeekspotCategories />
                  </AuthRoute>
                }
              />
            </>
          ) : (
            <>
              <Route
                exact
                path={ROUTES_FEEKAPP.LOGIN}
                element={
                  <AuthRoute login>
                    <LoginFeeker />
                  </AuthRoute>
                }
              />

              <Route
                exact
                path={ROUTES_FEEKAPP.REGISTRATION}
                element={<RegistrationFeekApp />}
              />

              <Route
                exact
                element={<ResetPassword />}
                path={ROUTES_FEEKAPP.RESET_PASSWORD}
              />

              <Route
                path={ROUTES_FEEKAPP.LANDING}
                element={<LandingFeekApp />}
              />
              {!userId && !localStorage.getItem('last_location') && (
                <Route path={'/'} element={<LandingFeekApp />} />
              )}

              <Route path="/" element={<OutletFeekapp />}>
                <Route path="*" element={<NotFoundFeekapp />} />

                <Route
                  element={<Explore />}
                  path={ROUTES_FEEKAPP.EXPLORE}
                >
                  <Route path="" element={<ExplorePromotions />} />
                  <Route
                    path={ROUTES_FEEKAPP.EXPLORE_REWARDS}
                    element={<ExploreRewards />}
                  />
                </Route>

                <Route
                  path={ROUTES_FEEKAPP.PROFILE_FEEKSPOT}
                  element={<FeekspotFeekApp />}
                />
                <Route
                  path={ROUTES_FEEKAPP.CAMPAIGN}
                  element={<CampaignFeekApp />}
                />
                <Route
                  path={ROUTES_FEEKAPP.SEARCHER}
                  element={<SearchFeekApp />}
                />
                <Route
                  path={ROUTES_FEEKAPP.SEARCH}
                  element={<SearchFeekApp />}
                />
                <Route path={ROUTES_FEEKAPP.MAP} element={<Map />} />
                <Route
                  path={ROUTES_FEEKAPP.MAP_RESULTS}
                  element={<MapResults />}
                />

                <Route
                  path={ROUTES_FEEKAPP.EXPERIENCE}
                  element={<Experience />}
                />
                <Route
                  path={ROUTES_FEEKAPP.PROFILE_FEEKER}
                  element={<ProfileFeeker />}
                />
              </Route>
              <Route
                path={'/'}
                element={
                  <AuthRoute>
                    <OutletFeekapp />
                  </AuthRoute>
                }
              >
                <Route
                  exact
                  path={ROUTES_FEEKAPP.NOTIFICATIONS}
                  element={<Notifications />}
                />
                <Route
                  exact
                  path={ROUTES_FEEKAPP.INFO}
                  element={<Info />}
                />
                <Route
                  path={ROUTES_FEEKAPP.PROFILE_FEEKER_EDIT}
                  element={<ProfileFeekerEdit />}
                />
                <Route
                  element={<Explore />}
                  path={ROUTES_FEEKAPP.EXPLORE}
                >
                  <Route path="" element={<ExplorePromotions />} />
                  <Route
                    path={ROUTES_FEEKAPP.EXPLORE_REWARDS}
                    element={<ExploreRewards />}
                  />
                </Route>
                <Route
                  path={ROUTES_FEEKAPP.CAMPAIGN}
                  element={<CampaignFeekApp />}
                />
                <Route
                  path={ROUTES_FEEKAPP.PROFILE_FEEKSPOT}
                  element={<FeekspotFeekApp />}
                />
                <Route
                  element={<Agenda />}
                  path={ROUTES_FEEKAPP.AGENDA}
                >
                  <Route path="" element={<Promotions />} />
                  <Route
                    path={ROUTES_FEEKAPP.AGENDA_PROMOTIONS}
                    element={<Promotions />}
                  />
                  <Route
                    path={ROUTES_FEEKAPP.AGENDA_REWARDS}
                    element={<Participations />}
                  />
                </Route>
              </Route>
            </>
          )}
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

function AuthRoute({ children, login = false, manager = false }) {
  const currentFeekToken = localStorage.getItem('currentFeekToken');

  return (login && currentFeekToken) ||
    (!login && (!children || !currentFeekToken)) ? (
    <Navigate
      to={
        manager
          ? ROUTES_MANAGER[
              !children || !currentFeekToken ? 'LANDING' : 'HOME'
            ]
          : ROUTES_FEEKAPP[!children ? 'LANDING' : 'EXPLORE']
      }
      replace
    />
  ) : (
    children
  );
}

AuthRoute.propTypes = {
  children: PropTypes.node,
  login: PropTypes.bool,
  manager: PropTypes.bool,
};

export default RootNavigator;
